import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Settings as SettingsIcon, Save, RefreshCw, Search,
  Lock, Unlock, Globe, Shield, DollarSign, Users,
  Eye, EyeOff, AlertCircle, CheckCircle, XCircle,
  ChevronDown, ChevronUp, Filter, Download, Copy,
  Key, Mail, Bell, Server, Database, Cpu,
  Wifi, CreditCard, Smartphone, Monitor, Calendar,
  BarChart3, FileText, Edit, Trash2, Plus,
  Moon, Sun, Globe as GlobeIcon, ShieldCheck,
  Zap, Terminal, Code, Layers, Palette,
  SaveAll, RotateCcw, Upload, Tag, Star
} from 'lucide-react';
import { AdminSettingsAPI, AdminAPIUtils } from '../services/api';

function Settings() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [categories, setCategories] = useState([]);
  const [modifiedSettings, setModifiedSettings] = useState({});
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [passwordVisibility, setPasswordVisibility] = useState({});

  useEffect(() => {
    fetchSettings();
    fetchCategories();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const data = await AdminSettingsAPI.getSettings();
      setSettings(data.settings || {});
      
      // Initialize expanded state for categories
      const initialExpanded = {};
      Object.keys(data.settings || {}).forEach(category => {
        initialExpanded[category] = true;
      });
      setExpandedCategories(initialExpanded);
    } catch (err) {
      const errorMsg = AdminAPIUtils.handleError(err, false).message;
      setError(errorMsg);
      if (err.status === 403) {
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const data = await AdminSettingsAPI.getCategories();
      setCategories(data.categories || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const handleSettingChange = (category, index, value) => {
    const settingKey = settings[category][index].setting_key;
    const newModifiedSettings = { ...modifiedSettings };
    
    if (!newModifiedSettings[category]) {
      newModifiedSettings[category] = {};
    }
    
    newModifiedSettings[category][settingKey] = value;
    setModifiedSettings(newModifiedSettings);
    
    // Update local state for immediate UI feedback
    const updatedSettings = { ...settings };
    updatedSettings[category][index].value = value;
    setSettings(updatedSettings);
  };

    
  const togglePasswordVisibility = (category, index) => {
    const key = `${category}-${index}`;
    setPasswordVisibility(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSaveSettings = async () => {
    if (Object.keys(modifiedSettings).length === 0) {
      setError('No changes to save');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      // Prepare settings array for bulk update
      const settingsToUpdate = [];
      Object.entries(modifiedSettings).forEach(([category, categorySettings]) => {
        Object.entries(categorySettings).forEach(([key, value]) => {
          settingsToUpdate.push({ key, value });
        });
      });
      
      await AdminSettingsAPI.updateMultipleSettings(settingsToUpdate);
      
      setSuccess('Settings saved successfully!');
      setModifiedSettings({});
      
      // Refresh settings to get any server-side modifications
      setTimeout(() => {
        fetchSettings();
      }, 1000);
      
    } catch (err) {
      const errorMsg = AdminAPIUtils.handleError(err, false).message;
      setError(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleResetSetting = async (category, settingIndex) => {
    const setting = settings[category][settingIndex];
    
    if (!window.confirm(`Reset "${setting.label}" to default value?`)) {
      return;
    }
    
    try {
      await AdminSettingsAPI.resetSetting(setting.setting_key);
      alert('Setting reset successfully!');
      fetchSettings();
    } catch (err) {
      AdminAPIUtils.handleError(err);
    }
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const getCategoryIcon = (category) => {
    const icons = {
      general: <GlobeIcon className="w-5 h-5" />,
      system: <Server className="w-5 h-5" />,
      users: <Users className="w-5 h-5" />,
      payment: <CreditCard className="w-5 h-5" />,
      security: <ShieldCheck className="w-5 h-5" />,
      email: <Mail className="w-5 h-5" />,
      notification: <Bell className="w-5 h-5" />,
      api: <Terminal className="w-5 h-5" />,
      database: <Database className="w-5 h-5" />,
      wallet: <DollarSign className="w-5 h-5" />,
      game: <Smartphone className="w-5 h-5" />,
      maintenance: <Wifi className="w-5 h-5" />,
      analytics: <BarChart3 className="w-5 h-5" />,
      logging: <FileText className="w-5 h-5" />,
      theme: <Palette className="w-5 h-5" />,
      performance: <Zap className="w-5 h-5" />,
      advanced: <Code className="w-5 h-5" />
    };
    
    return icons[category] || <SettingsIcon className="w-5 h-5" />;
  };

  const getCategoryColor = (category) => {
    const colors = {
      general: 'from-blue-500 to-cyan-500',
      system: 'from-gray-500 to-slate-600',
      users: 'from-emerald-500 to-green-500',
      payment: 'from-amber-500 to-yellow-500',
      security: 'from-rose-500 to-red-500',
      email: 'from-purple-500 to-violet-500',
      notification: 'from-pink-500 to-rose-500',
      api: 'from-indigo-500 to-blue-600',
      database: 'from-teal-500 to-cyan-600',
      wallet: 'from-orange-500 to-amber-500',
      game: 'from-sky-500 to-blue-400',
      maintenance: 'from-lime-500 to-green-400',
      analytics: 'from-violet-500 to-purple-600',
      logging: 'from-slate-500 to-gray-600',
      theme: 'from-fuchsia-500 to-pink-600',
      performance: 'from-cyan-500 to-blue-500',
      advanced: 'from-gray-600 to-slate-700'
    };
    
    return colors[category] || 'from-gray-500 to-slate-600';
  };

  const renderSettingField = (setting, category, index) => {
    const isModified = modifiedSettings[category] && 
                      modifiedSettings[category][setting.setting_key] !== undefined;
    
    const fieldClasses = `w-full px-4 py-3 bg-gray-900/50 border rounded-xl text-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors ${
      isModified ? 'border-amber-500 bg-amber-500/5' : 'border-gray-800'
    }`;
    
    switch (setting.data_type) {
      case 'boolean':
        return (
          <div className="flex items-center">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={setting.value}
                onChange={(e) => handleSettingChange(category, index, e.target.checked)}
                className="sr-only peer"
              />
              <div className={`w-12 h-6 bg-gray-700 peer-focus:ring-2 peer-focus:ring-sky-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600 ${isModified ? 'ring-2 ring-amber-500' : ''}`}></div>
              <span className="ml-3 text-sm font-medium text-gray-300">
                {setting.value ? 'Enabled' : 'Disabled'}
              </span>
            </label>
          </div>
        );
        
      case 'number':
        return (
          <div className="relative">
            <input
              type="number"
              value={setting.value}
              onChange={(e) => handleSettingChange(category, index, parseFloat(e.target.value))}
              className={fieldClasses}
              step={setting.options?.step || 'any'}
              min={setting.options?.min}
              max={setting.options?.max}
            />
            {setting.options?.suffix && (
              <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                {setting.options.suffix}
              </span>
            )}
          </div>
        );
        
      case 'json':
      case 'array':
      case 'object':
        return (
          <div>
            <textarea
              value={typeof setting.value === 'object' ? JSON.stringify(setting.value, null, 2) : setting.value}
              onChange={(e) => {
                try {
                  const parsed = JSON.parse(e.target.value);
                  handleSettingChange(category, index, parsed);
                } catch (err) {
                  handleSettingChange(category, index, e.target.value);
                }
              }}
              className={`${fieldClasses} font-mono text-sm`}
              rows="4"
              placeholder="Enter JSON..."
            />
            <p className="text-xs text-charcoal-gray mt-2">
              {setting.data_type.toUpperCase()} - Use valid JSON format
            </p>
          </div>
        );
        
      case 'select':
        return (
          <select
            value={setting.value}
            onChange={(e) => handleSettingChange(category, index, e.target.value)}
            className={fieldClasses}
          >
            {setting.options?.allowed_values?.map((option) => (
              <option key={option.value} value={option.value} className="bg-night-blue text-white">
                {option.label || option.value}
              </option>
            ))}
          </select>
        );
        
        default: // string
      if (setting.options?.type === 'password') {
        const key = `${category}-${index}`;
        const showPassword = passwordVisibility[key] || false;
        
        return (
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              value={setting.value}
              onChange={(e) => handleSettingChange(category, index, e.target.value)}
              className={`${fieldClasses} pr-10`}
              placeholder={setting.placeholder || ''}
            />
            <button
              type="button"
              onClick={() => togglePasswordVisibility(category, index)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        );
      }
        
        if (setting.options?.type === 'textarea') {
          return (
            <textarea
              value={setting.value}
              onChange={(e) => handleSettingChange(category, index, e.target.value)}
              className={`${fieldClasses} resize-none`}
              rows="3"
              placeholder={setting.placeholder || ''}
            />
          );
        }
        
        if (setting.options?.type === 'email') {
          return (
            <div className="relative">
              <input
                type="email"
                value={setting.value}
                onChange={(e) => handleSettingChange(category, index, e.target.value)}
                className={`${fieldClasses} pr-10`}
                placeholder={setting.placeholder || ''}
              />
              <Mail className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            </div>
          );
        }
        
        if (setting.options?.type === 'color') {
          return (
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={setting.value}
                onChange={(e) => handleSettingChange(category, index, e.target.value)}
                className="w-12 h-12 cursor-pointer rounded-lg border-0"
              />
              <input
                type="text"
                value={setting.value}
                onChange={(e) => handleSettingChange(category, index, e.target.value)}
                className={fieldClasses}
                placeholder="#000000"
              />
            </div>
          );
        }
        
        return (
          <input
            type="text"
            value={setting.value}
            onChange={(e) => handleSettingChange(category, index, e.target.value)}
            className={fieldClasses}
            placeholder={setting.placeholder || ''}
          />
        );
    }
  };

  if (!AdminSettingsAPI.canManageSettings()) {
    return (
      <div className="flex justify-center items-center h-screen bg-deep-black">
        <div className="text-center">
          <Shield className="w-20 h-20 text-rose-400 mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-white mb-4">Access Denied</h2>
          <p className="text-charcoal-gray mb-8 max-w-md">You need admin privileges to access settings.</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-3 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white rounded-xl transition-all duration-300 hover:scale-105"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const totalModifiedCount = Object.values(modifiedSettings).reduce((total, category) => 
    total + Object.keys(category).length, 0
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold bg-gradient-to-r from-sky-400 to-blue-500 bg-clip-text text-transparent">
            System Settings
          </h1>
          <p className="text-charcoal-gray mt-1">Configure application settings and preferences</p>
        </div>
        <div className="flex items-center space-x-3">
          {totalModifiedCount > 0 && (
            <div className="flex items-center px-4 py-2 bg-amber-500/10 border border-amber-500/20 rounded-xl">
              <span className="text-amber-400 text-sm font-medium">
                {totalModifiedCount} unsaved change{totalModifiedCount !== 1 ? 's' : ''}
              </span>
            </div>
          )}
          <button
            onClick={handleSaveSettings}
            disabled={saving || totalModifiedCount === 0}
            className="px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] flex items-center gap-2"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                Saving...
              </>
            ) : (
              <>
                <SaveAll className="w-4 h-4" />
                Save All Changes
              </>
            )}
          </button>
          <button
            onClick={fetchSettings}
            className="px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl transition-all duration-300 hover:scale-[1.02] flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="p-4 bg-rose-500/10 border border-rose-500/20 rounded-xl backdrop-blur-sm">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-rose-400 mr-3" />
            <div>
              <p className="text-rose-300 font-medium">Error</p>
              <p className="text-sm text-rose-200/80 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}
      
      {success && (
        <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl backdrop-blur-sm">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 text-emerald-400 mr-3" />
            <div>
              <p className="text-emerald-300 font-medium">Success</p>
              <p className="text-sm text-emerald-200/80 mt-1">{success}</p>
            </div>
          </div>
        </div>
      )}

      {/* Settings Navigation & Content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Categories Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-night-blue/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
            <div className="mb-6">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-charcoal-gray w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search settings..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white placeholder-charcoal-gray focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                />
              </div>
            </div>

            <div className="space-y-2">
              <button
                onClick={() => setActiveCategory('all')}
                className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-300 flex items-center justify-between ${
                  activeCategory === 'all' 
                    ? 'bg-sky-500/20 border border-sky-500/30 text-white' 
                    : 'hover:bg-gray-800/50 text-gray-300 hover:text-white'
                }`}
              >
                <div className="flex items-center gap-3">
                  <SettingsIcon className="w-4 h-4" />
                  <span>All Categories</span>
                </div>
                <span className="text-xs bg-gray-800/50 px-2 py-1 rounded">
                  {Object.values(settings).reduce((total, category) => total + category.length, 0)}
                </span>
              </button>

              {categories.map((category) => {
                const categorySettings = settings[category] || [];
                const modifiedInCategory = modifiedSettings[category] ? Object.keys(modifiedSettings[category]).length : 0;
                
                return (
                  <button
                    key={category}
                    onClick={() => setActiveCategory(category)}
                    className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-300 flex items-center justify-between ${
                      activeCategory === category 
                        ? 'bg-sky-500/20 border border-sky-500/30 text-white' 
                        : 'hover:bg-gray-800/50 text-gray-300 hover:text-white'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg bg-gradient-to-br ${getCategoryColor(category)}`}>
                        {getCategoryIcon(category)}
                      </div>
                      <div className="text-left">
                        <span className="block capitalize">{category.replace(/_/g, ' ')}</span>
                        <span className="text-xs text-charcoal-gray">
                          {categorySettings.length} setting{categorySettings.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                    {modifiedInCategory > 0 && (
                      <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-1 rounded">
                        {modifiedInCategory}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {AdminSettingsAPI.canModifySettings() && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="w-full mt-6 px-4 py-3 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white rounded-xl transition-all duration-300 hover:scale-[1.02] flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Setting
              </button>
            )}

            <div className="mt-6 pt-6 border-t border-gray-800">
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="w-full text-left px-4 py-2 text-charcoal-gray hover:text-white transition-colors flex items-center gap-2"
              >
                <Code className="w-4 h-4" />
                {showAdvanced ? 'Hide Advanced' : 'Show Advanced'}
              </button>
            </div>
          </div>
        </div>

        {/* Settings Content */}
        <div className="lg:col-span-3">
          {loading ? (
            <div className="flex justify-center items-center h-96 bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl">
              <div className="text-center">
                <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-sky-500 mx-auto mb-6"></div>
                <p className="text-charcoal-gray">Loading settings...</p>
              </div>
            </div>
          ) : Object.keys(settings).length === 0 ? (
            <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-12 text-center">
              <SettingsIcon className="w-20 h-20 text-gray-700 mx-auto mb-6" />
              <h3 className="text-xl font-medium text-white mb-4">No settings found</h3>
              <p className="text-charcoal-gray mb-8 max-w-md mx-auto">Settings will appear here once configured.</p>
              {AdminSettingsAPI.canModifySettings() && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="px-6 py-3 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white rounded-xl transition-all duration-300 hover:scale-105"
                >
                  <Plus className="w-4 h-4 inline-block mr-2" />
                  Create First Setting
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(settings)
                .filter(([category]) => 
                  activeCategory === 'all' || category === activeCategory
                )
                .filter(([category, categorySettings]) =>
                  searchTerm === '' || 
                  category.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  categorySettings.some(setting => 
                    setting.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    setting.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    setting.setting_key.toLowerCase().includes(searchTerm.toLowerCase())
                  )
                )
                .map(([category, categorySettings]) => {
                  const modifiedInCategory = modifiedSettings[category] ? Object.keys(modifiedSettings[category]).length : 0;
                  
                  return (
                    <div key={category} className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl overflow-hidden">
                      {/* Category Header */}
                      <div 
                        className={`px-6 py-5 border-b border-gray-800 cursor-pointer transition-all duration-300 hover:bg-gray-800/20 ${
                          expandedCategories[category] ? 'bg-gray-800/20' : ''
                        }`}
                        onClick={() => toggleCategory(category)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`p-3 rounded-xl bg-gradient-to-br ${getCategoryColor(category)} shadow-lg`}>
                              {getCategoryIcon(category)}
                            </div>
                            <div>
                              <h3 className="font-bold text-white capitalize text-lg">
                                {category.replace(/_/g, ' ')}
                                {modifiedInCategory > 0 && (
                                  <span className="ml-3 text-amber-400 text-sm bg-amber-500/10 px-2 py-1 rounded">
                                    {modifiedInCategory} modified
                                  </span>
                                )}
                              </h3>
                              <p className="text-sm text-charcoal-gray mt-1">
                                {categorySettings.length} setting{categorySettings.length !== 1 ? 's' : ''}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-gray-400">
                              {expandedCategories[category] ? (
                                <ChevronUp className="w-5 h-5" />
                              ) : (
                                <ChevronDown className="w-5 h-5" />
                              )}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Category Settings */}
                      {expandedCategories[category] && (
                        <div className="p-6">
                          <div className="space-y-8">
                            {categorySettings
                              .filter(setting => 
                                searchTerm === '' ||
                                setting.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                setting.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                setting.setting_key.toLowerCase().includes(searchTerm.toLowerCase())
                              )
                              .map((setting, index) => {
                                const isModified = modifiedSettings[category] && 
                                                  modifiedSettings[category][setting.setting_key] !== undefined;
                                
                                return (
                                  <div key={setting.setting_key} className="pb-8 border-b border-gray-800/50 last:border-0 last:pb-0">
                                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                                      <div>
                                        <div className="space-y-3">
                                          <div className="flex items-start justify-between">
                                            <div>
                                              <label className="block text-lg font-semibold text-white mb-2 flex items-center gap-2">
                                                {setting.label}
                                                {isModified && (
                                                  <span className="inline-block w-2 h-2 bg-amber-400 rounded-full animate-pulse"></span>
                                                )}
                                                {setting.is_encrypted && (
                                                  <Lock className="w-4 h-4 text-blue-400" title="Encrypted" />
                                                )}
                                              </label>
                                              {setting.description && (
                                                <p className="text-sm text-charcoal-gray">
                                                  {setting.description}
                                                </p>
                                              )}
                                            </div>
                                          </div>
                                          
                                          <div className="flex items-center gap-2">
                                            <span className="text-xs px-3 py-1.5 bg-gray-800/50 text-gray-300 rounded-lg">
                                              {setting.data_type}
                                            </span>
                                            <code className="text-xs text-gray-400 font-mono px-3 py-1.5 bg-gray-900/50 rounded-lg">
                                              {setting.setting_key}
                                            </code>
                                          </div>
                                          
                                          <div className="text-xs text-charcoal-gray flex items-center gap-2">
                                            {setting.is_public ? (
                                              <span className="flex items-center gap-1">
                                                <Globe className="w-3 h-3" />
                                                Public setting
                                              </span>
                                            ) : (
                                              <span className="flex items-center gap-1">
                                                <Shield className="w-3 h-3" />
                                                Admin only
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </div>

                                      <div className="lg:col-span-2">
                                        <div className="space-y-4">
                                          {renderSettingField(setting, category, index)}
                                          
                                          <div className="flex items-center justify-between pt-4">
                                            {setting.default_value !== null && setting.default_value !== undefined && (
                                              <button
                                                onClick={() => handleResetSetting(category, index)}
                                                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-xl transition-all duration-300 hover:scale-105 flex items-center gap-2"
                                                title="Reset to default"
                                              >
                                                <RotateCcw className="w-4 h-4" />
                                                Reset to Default
                                              </button>
                                            )}
                                            
                                            <div className="flex items-center gap-2">
                                              {showAdvanced && (
                                                <>
                                                  <button
                                                    onClick={() => {
                                                      navigator.clipboard.writeText(setting.setting_key);
                                                      alert('Key copied to clipboard!');
                                                    }}
                                                    className="px-3 py-2 bg-gray-800/50 hover:bg-gray-700 text-gray-300 hover:text-white rounded-lg transition-colors flex items-center gap-2"
                                                    title="Copy key"
                                                  >
                                                    <Copy className="w-3 h-3" />
                                                  </button>
                                                  {AdminSettingsAPI.canModifySettings() && (
                                                    <button
                                                      onClick={() => {
                                                        if (window.confirm(`Delete setting "${setting.label}"?`)) {
                                                          // Implement delete functionality
                                                        }
                                                      }}
                                                      className="px-3 py-2 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 hover:text-rose-300 rounded-lg transition-colors flex items-center gap-2"
                                                      title="Delete setting"
                                                    >
                                                      <Trash2 className="w-3 h-3" />
                                                    </button>
                                                  )}
                                                </>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </div>

      {/* Advanced Tools */}
      {showAdvanced && (
        <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
          <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
            <Code className="w-5 h-5" />
            Advanced Tools
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <button
              onClick={() => {
                const confirm = window.confirm('Export all settings as JSON?');
                if (confirm) {
                  const dataStr = JSON.stringify(settings, null, 2);
                  const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
                  const exportFileDefaultName = 'settings_export_' + new Date().toISOString().split('T')[0] + '.json';
                  const linkElement = document.createElement('a');
                  linkElement.setAttribute('href', dataUri);
                  linkElement.setAttribute('download', exportFileDefaultName);
                  linkElement.click();
                }
              }}
              className="p-4 bg-gray-800/50 hover:bg-gray-800 rounded-xl border border-gray-800 transition-all duration-300 hover:scale-[1.02] flex flex-col items-center gap-3"
            >
              <Download className="w-6 h-6 text-sky-400" />
              <span className="text-sm font-medium text-white">Export JSON</span>
            </button>
            <button
              onClick={() => {
                const allSettings = Object.values(settings).flat();
                const text = allSettings.map(s => `${s.setting_key}=${s.value}`).join('\n');
                navigator.clipboard.writeText(text);
                alert('Settings copied to clipboard!');
              }}
              className="p-4 bg-gray-800/50 hover:bg-gray-800 rounded-xl border border-gray-800 transition-all duration-300 hover:scale-[1.02] flex flex-col items-center gap-3"
            >
              <Copy className="w-6 h-6 text-emerald-400" />
              <span className="text-sm font-medium text-white">Copy All</span>
            </button>
            <button
              onClick={() => {
                if (window.confirm('Clear all unsaved changes?')) {
                  setModifiedSettings({});
                  fetchSettings();
                }
              }}
              className="p-4 bg-gray-800/50 hover:bg-gray-800 rounded-xl border border-gray-800 transition-all duration-300 hover:scale-[1.02] flex flex-col items-center gap-3"
            >
              <RotateCcw className="w-6 h-6 text-amber-400" />
              <span className="text-sm font-medium text-white">Discard Changes</span>
            </button>
            <button
              onClick={() => {
                if (window.confirm('This will reset ALL settings to default values. Continue?')) {
                  AdminSettingsAPI.resetAllSettings()
                    .then(() => {
                      alert('All settings reset to default!');
                      fetchSettings();
                    })
                    .catch(AdminAPIUtils.handleError);
                }
              }}
              className="p-4 bg-rose-500/10 hover:bg-rose-500/20 rounded-xl border border-rose-500/20 transition-all duration-300 hover:scale-[1.02] flex flex-col items-center gap-3"
            >
              <Trash2 className="w-6 h-6 text-rose-400" />
              <span className="text-sm font-medium text-white">Reset All</span>
            </button>
          </div>
        </div>
      )}

      {/* Create Setting Modal */}
      {showCreateModal && (
        <CreateSettingModal
          onClose={() => setShowCreateModal(false)}
          onCreate={() => {
            setShowCreateModal(false);
            fetchSettings();
            fetchCategories();
          }}
        />
      )}
    </div>
  );
}

// Create Setting Modal Component
function CreateSettingModal({ onClose, onCreate }) {
  const [formData, setFormData] = useState({
    key: '',
    value: '',
    data_type: 'string',
    category: 'general',
    label: '',
    description: '',
    is_public: false,
    is_encrypted: false,
    options: {}
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate
    const newErrors = {};
    if (!formData.key.trim()) newErrors.key = 'Key is required';
    if (!formData.label.trim()) newErrors.label = 'Label is required';
    if (!formData.category.trim()) newErrors.category = 'Category is required';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setLoading(true);
    
    try {
      await AdminSettingsAPI.createSetting(formData);
      alert('Setting created successfully!');
      onCreate();
    } catch (err) {
      AdminAPIUtils.handleError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-night-blue border border-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-lg font-bold text-white">Create New Setting</h3>
              <p className="text-sm text-charcoal-gray mt-1">Add a new configuration setting</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors p-1.5 hover:bg-gray-800/50 rounded-lg"
            >
              <XCircle className="w-5 h-5" />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Setting Key *
                </label>
                <input
                  type="text"
                  value={formData.key}
                  onChange={(e) => handleChange('key', e.target.value)}
                  className={`w-full px-4 py-3 bg-gray-900/50 border rounded-xl text-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors ${
                    errors.key ? 'border-rose-500' : 'border-gray-800'
                  }`}
                  placeholder="e.g., site_name"
                />
                {errors.key && (
                  <p className="mt-2 text-sm text-rose-400">{errors.key}</p>
                )}
                <p className="mt-2 text-xs text-charcoal-gray">
                  Unique identifier (use underscores, no spaces)
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Data Type *
                </label>
                <select
                  value={formData.data_type}
                  onChange={(e) => handleChange('data_type', e.target.value)}
                  className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                >
                  <option value="string">String</option>
                  <option value="number">Number</option>
                  <option value="boolean">Boolean</option>
                  <option value="json">JSON</option>
                  <option value="array">Array</option>
                  <option value="select">Select</option>
                </select>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Label *
              </label>
              <input
                type="text"
                value={formData.label}
                onChange={(e) => handleChange('label', e.target.value)}
                className={`w-full px-4 py-3 bg-gray-900/50 border rounded-xl text-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors ${
                  errors.label ? 'border-rose-500' : 'border-gray-800'
                }`}
                placeholder="Site Name"
              />
              {errors.label && (
                <p className="mt-2 text-sm text-rose-400">{errors.label}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                rows="2"
                placeholder="Brief description of this setting..."
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Category *
                </label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => handleChange('category', e.target.value)}
                  className={`w-full px-4 py-3 bg-gray-900/50 border rounded-xl text-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors ${
                    errors.category ? 'border-rose-500' : 'border-gray-800'
                  }`}
                  placeholder="general"
                />
                {errors.category && (
                  <p className="mt-2 text-sm text-rose-400">{errors.category}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Default Value
                </label>
                <input
                  type="text"
                  value={formData.value}
                  onChange={(e) => handleChange('value', e.target.value)}
                  className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                  placeholder="Default value"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_public}
                  onChange={(e) => handleChange('is_public', e.target.checked)}
                  className="h-4 w-4 text-sky-500 focus:ring-sky-500 border-gray-700 rounded bg-gray-900"
                />
                <label className="ml-2 text-sm text-gray-300">
                  Public Setting (visible to frontend)
                </label>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_encrypted}
                  onChange={(e) => handleChange('is_encrypted', e.target.checked)}
                  className="h-4 w-4 text-sky-500 focus:ring-sky-500 border-gray-700 rounded bg-gray-900"
                />
                <label className="ml-2 text-sm text-gray-300">
                  Encrypt Value (for sensitive data)
                </label>
              </div>
            </div>
            
            <div className="flex justify-end gap-3 pt-6 border-t border-gray-800">
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl transition-all duration-300"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-5 py-2.5 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white rounded-xl transition-all duration-300 hover:scale-105 flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Create Setting
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Settings;